import { create } from 'zustand';
import { createSelectors, withDevtools, StoreName } from '../config';
import {
  createInitialData,
  createDataActions,
  createSearchActions,
  initialSearchSlice,
} from '../types';
import { companyClient } from 'shared/domain/company/companyClient';
import type { CompanyModel } from 'shared/domain/company/types/model';
import { EntityName } from 'shared/domain/entityName';
import { changeEntityDeleted, createEntityCollectionActions } from '../entityCollection';
import type { CompaniesStore } from './types';

export const companySearchableFields = [
  'shortLabel',
  'longLabel',
  'taxNumber',
  'correspondenceAddress.postalCode',
  'correspondenceAddress.streetAndNumber',
  'registeredOfficeAddress.postalCode',
  'registeredOfficeAddress.streetAndNumber',
];

const useCompaniesStoreBase = create<CompaniesStore>()(
  withDevtools((set, get) => {
    /** Archives or restores at once; undone when the server rejects it. */
    const changeCompanyDeleted = (companyId: string, deleted: boolean): void =>
      changeEntityDeleted({
        deleted,
        markDeleted: (value) => get().actions.updateCompanyDeleted(companyId, value),
        sendChange: (value) => (value ? companyClient.remove(companyId) : companyClient.restore(companyId)),
        entityLabel: 'a company',
      });

    return {
      data: createInitialData<CompanyModel>(),
      search: initialSearchSlice,

      actions: {
        ...createDataActions<CompanyModel, CompaniesStore>(set),
        ...createSearchActions<CompaniesStore>(set),
        ...createEntityCollectionActions(
          { client: companyClient, entityName: EntityName.companies },
          () => get().actions
        ),
        archive: (companyId) => changeCompanyDeleted(companyId, true),
        restore: (companyId) => changeCompanyDeleted(companyId, false),

        updateCompanyDeleted: (companyId, deleted) =>
          set((state) => ({
            ...state,
            data: {
              ...state.data,
              items: state.data.items.map((company) =>
                company._id === companyId ? { ...company, deleted } : company
              ),
            },
          })),

        reset: () =>
          set((state) => ({
            ...state,
            data: createInitialData<CompanyModel>(),
            search: initialSearchSlice,
          })),
      },
    };
  }, StoreName.Companies)
);

export const useCompaniesStore = createSelectors(useCompaniesStoreBase);

export const getCompaniesStoreState = (): CompaniesStore =>
  useCompaniesStore.getState();
